import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  query,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../../firebase";
import { useNavigate } from "react-router-dom";
import {
  FaChevronDown,
  FaTimes,
  FaCamera,
  FaTrashAlt,
  FaCheckCircle,
  FaSpinner,
  FaArrowRight,
  FaPlus,
} from "react-icons/fa";
import "./CreateRetailerOrder.scss";

// --- UTILITY: Generate Order ID ---
const formatOrderIdPrefix = (date) => {
  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  return `SV${year}${month}`;
};

const generateOrderId = async () => {
  const currentDate = new Date();
  const monthPrefix = formatOrderIdPrefix(currentDate);
  const currentDay = currentDate.getDate().toString().padStart(2, "0");
  const snapshot = await getDocs(query(collection(db, "partyOrders")));
  let highestSequence = 0;

  snapshot.docs.forEach((doc) => {
    const orderId = doc.data().orderId;
    if (
      orderId &&
      typeof orderId === "string" &&
      orderId.startsWith(monthPrefix)
    ) {
      const sequence = parseInt(orderId.substring(8), 10);
      if (!isNaN(sequence) && sequence > highestSequence) {
        highestSequence = sequence;
      }
    }
  });
  return `${monthPrefix}${currentDay}${(highestSequence + 1).toString().padStart(3, "0")}`;
};

// --- SMART CATEGORY DATA ---
const CATEGORIES = [
  { id: "Mangalsutra", label: "Mangalsutra", unit: "Inches" },
  { id: "Necklace", label: "Necklace", unit: "Inches" },
  { id: "Bangle", label: "Bangles", unit: "Size" },
  { id: "Ring", label: "Ring", unit: "No." },
  { id: "Earrings", label: "Earrings", unit: "Custom" },
  { id: "Pendant", label: "Pendant", unit: "Inches" },
];

const CreateRetailerOrder = () => {
  const navigate = useNavigate();

  const session = useMemo(() => {
    const stored = localStorage.getItem("retailerSession");
    return stored ? JSON.parse(stored) : { id: "GUEST" };
  }, []);

  const [images, setImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [isClosing, setIsClosing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0]);
  const [isCustomCat, setIsCustomCat] = useState(false);
  const [customCatName, setCustomCatName] = useState("");
  const [manufacturingType, setManufacturingType] = useState("Handmade");

  const [formData, setFormData] = useState({
    weight: "",
    size: "",
    carat: "22K",
    stone: "None",
    qty: "1",
    deliveryDate: "",
    instructions: "",
  });

  useEffect(() => {
    if (!isCustomCat) {
      if (["Ring", "Earrings"].includes(activeCategory.id)) {
        setManufacturingType("Casting");
      } else {
        setManufacturingType("Handmade");
      }
    }
  }, [activeCategory, isCustomCat]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => navigate(-1), 300);
  };

  const [swipeProgress, setSwipeProgress] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const trackRef = useRef(null);

  const handleTouchStart = () => {
    if (!isSubmitting) setIsSwiping(true);
  };

  const handleTouchMove = (e) => {
    if (!isSwiping || isSubmitting) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const track = trackRef.current.getBoundingClientRect();
    const maxSwipe = track.width - 56;
    let newProgress = clientX - track.left - 28;

    if (newProgress < 0) newProgress = 0;
    if (newProgress > maxSwipe) newProgress = maxSwipe;

    setSwipeProgress(newProgress);

    if (newProgress >= maxSwipe * 0.95) {
      setIsSwiping(false);
      setSwipeProgress(maxSwipe);
      handleSubmit();
    }
  };

  const handleTouchEnd = () => {
    if (isSwiping && !isSubmitting) {
      setIsSwiping(false);
      setSwipeProgress(0);
    }
  };

  const handleImageCapture = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const newUrls = files.map((f) => URL.createObjectURL(f));
    setImages((prev) => [...prev, ...files]);
    setPreviewUrls((prev) => [...prev, ...newUrls]);
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!images.length && !formData.instructions) {
      alert("Please add a photo or instructions.");
      setSwipeProgress(0);
      return;
    }

    const finalProductName = isCustomCat
      ? customCatName || "Custom Item"
      : activeCategory.id;
    setIsSubmitting(true);

    try {
      const orderId = await generateOrderId();

      // We surface summary fields (totalWeight, totalSize, primaryImage) at the root level
      // so the Firebase Cloud Function can easily read them for the WhatsApp message.
      let docData = {
        orderId,
        partyId: session.id,
        deliveryDate: formData.deliveryDate || "TBD",
        comments: formData.instructions,
        status: "Pending",
        totalQuantity: parseInt(formData.qty) || 1,
        totalWeight: formData.weight || "TBD",
        totalSize: formData.size || "TBD",
        primaryImage: null,
        createdAt: serverTimestamp(),
        unseenCount: 1,
      };

      if (manufacturingType === "Handmade") {
        docData.orderType = "Single";
        docData.product = finalProductName;
        docData.singleItems = [
          {
            weight: formData.weight || "TBD",
            length: formData.size || "TBD",
            lengthUnit: isCustomCat ? "Unit" : activeCategory.unit,
            quantity: parseInt(formData.qty) || 1,
            itemStatus: "Pending",
            fileUrls: [],
          },
        ];

        if (images.length > 0) {
          const uploadPromises = images.map(async (file, i) => {
            const sRef = ref(
              storage,
              `orders_images/rt_${session.id}_${Date.now()}_${i}_${file.name}`,
            );
            const snap = await uploadBytes(sRef, file);
            return await getDownloadURL(snap.ref);
          });
          const urls = await Promise.all(uploadPromises);
          docData.singleItems[0].fileUrls = urls;
          docData.primaryImage = urls[0];
        }
      } else {
        docData.orderType = "Casting";
        docData.files = [];
        docData.castingDetails = {
          item: finalProductName,
          carat: formData.carat,
          weight: formData.weight || "TBD",
          stone: formData.stone,
          budget: "",
          finalWeight: "",
          size: formData.size || "TBD",
        };

        if (images.length > 0) {
          const uploadPromises = images.map(async (file, i) => {
            const sRef = ref(
              storage,
              `orders_images/rt_${session.id}_${Date.now()}_${i}_${file.name}`,
            );
            const snap = await uploadBytes(sRef, file);
            return await getDownloadURL(snap.ref);
          });
          docData.files = await Promise.all(uploadPromises);
          docData.primaryImage = docData.files[0];
        }
      }

      // Save to Firestore. The Cloud Function will detect this instantly.
      await addDoc(collection(db, "partyOrders"), docData);

      setIsSuccess(true);
      setTimeout(() => handleClose(), 2000);
    } catch (error) {
      console.error("🔥 Order Submission Failed:", error);
      alert(
        "Failed to place order. Please check your connection and try again.",
      );
      setIsSubmitting(false);
      setSwipeProgress(0);
    }
  };

  if (isSuccess) {
    return (
      <div className={`v-rt-create-overlay ${isClosing ? "closing" : ""}`}>
        <div className="v-rt-create-success-card pop-in">
          <FaCheckCircle className="check-icon" />
          <h2>Order Sent!</h2>
          <p>Factory has received your requirement.</p>
        </div>
      </div>
    );
  }

  // --- RENDERING FORM (Matches your exact UI layout) ---
  return (
    <div className={`v-rt-create-overlay ${isClosing ? "closing" : ""}`}>
      <div className="v-rt-create-backdrop" onClick={handleClose}></div>

      <div className={`v-rt-create-wrapper ${isClosing ? "closing" : ""}`}>
        <header className="v-rt-create-header">
          <div className="mobile-pull-bar"></div>
          <div className="header-content">
            <h2>New Requirement</h2>
            <button
              className="close-btn"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              <FaTimes className="desktop-icon" />
              <FaChevronDown className="mobile-icon" />
            </button>
          </div>
        </header>

        <div className="v-rt-create-scroll-body fade-in-up">
          <section className="v-rt-create-section">
            <div className="v-rt-manufacturing-toggle">
              <button
                type="button"
                className={`v-rt-toggle-btn ${manufacturingType === "Handmade" ? "active handmade" : ""}`}
                onClick={() => setManufacturingType("Handmade")}
              >
                ✋ Handmade
              </button>
              <button
                type="button"
                className={`v-rt-toggle-btn ${manufacturingType === "Casting" ? "active casting" : ""}`}
                onClick={() => setManufacturingType("Casting")}
              >
                🔥 Casting
              </button>
            </div>
          </section>

          <section className="v-rt-create-section">
            <label className="section-label">1. Product Category</label>
            <div className="category-scroll-chips">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`category-chip ${!isCustomCat && activeCategory.id === cat.id ? "active" : ""}`}
                  onClick={() => {
                    setIsCustomCat(false);
                    setActiveCategory(cat);
                    setFormData({ ...formData, size: "" });
                  }}
                >
                  {cat.label}
                </button>
              ))}
              {isCustomCat ? (
                <input
                  autoFocus
                  type="text"
                  className="custom-cat-input pop-in"
                  placeholder="Type Name..."
                  value={customCatName}
                  onChange={(e) => setCustomCatName(e.target.value)}
                />
              ) : (
                <button
                  type="button"
                  className="category-chip custom-add"
                  onClick={() => setIsCustomCat(true)}
                >
                  <FaPlus /> Custom
                </button>
              )}
            </div>
          </section>

          <section className="v-rt-create-section">
            <label className="section-label">2. Design Reference</label>
            {previewUrls.length === 0 ? (
              <div
                className="upload-empty-zone"
                onClick={() => fileInputRef.current.click()}
              >
                <div className="camera-circle">
                  <FaCamera />
                </div>
                <span>Tap to Attach Design</span>
                <p>Camera or Gallery</p>
              </div>
            ) : (
              <div className="photo-gallery-zone">
                {previewUrls.map((url, i) => (
                  <div key={i} className="photo-thumbnail pop-in">
                    <img src={url} alt="Order Ref" />
                    <button
                      type="button"
                      className="delete-badge"
                      onClick={() => removeImage(i)}
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                ))}
                {previewUrls.length < 5 && (
                  <div
                    className="add-more-thumbnail"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <FaCamera />
                  </div>
                )}
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleImageCapture}
            />
          </section>

          <section className="v-rt-create-section specs-grid">
            <div className="spec-input-group">
              <label>Est. Weight (Grams)</label>
              <input
                type="number"
                placeholder="e.g. 30"
                value={formData.weight}
                onChange={(e) =>
                  setFormData({ ...formData, weight: e.target.value })
                }
              />
            </div>

            <div className="spec-input-group">
              <label>
                Size{" "}
                {!isCustomCat &&
                  activeCategory.unit &&
                  `(${activeCategory.unit})`}
              </label>
              <input
                type="text"
                placeholder={
                  !isCustomCat && activeCategory.unit
                    ? `e.g. 18 ${activeCategory.unit}`
                    : "Specify size..."
                }
                value={formData.size}
                onChange={(e) =>
                  setFormData({ ...formData, size: e.target.value })
                }
              />
            </div>

            {manufacturingType === "Casting" && (
              <>
                <div className="spec-input-group">
                  <label>Purity</label>
                  <select
                    value={formData.carat}
                    onChange={(e) =>
                      setFormData({ ...formData, carat: e.target.value })
                    }
                  >
                    <option value="22K">22K</option>
                    <option value="18K">18K</option>
                    <option value="14K">14K</option>
                  </select>
                </div>
                <div className="spec-input-group">
                  <label>Stone</label>
                  <select
                    value={formData.stone}
                    onChange={(e) =>
                      setFormData({ ...formData, stone: e.target.value })
                    }
                  >
                    <option value="None">None</option>
                    <option value="Diamond">Diamond</option>
                    <option value="AD/CZ">AD / CZ</option>
                    <option value="Color Stone">Color Stone</option>
                  </select>
                </div>
              </>
            )}

            <div className="spec-input-group">
              <label>Required By (Date)</label>
              <input
                type="date"
                value={formData.deliveryDate}
                onChange={(e) =>
                  setFormData({ ...formData, deliveryDate: e.target.value })
                }
              />
            </div>

            <div className="spec-input-group">
              <label>Quantity</label>
              <div className="qty-stepper">
                <button
                  type="button"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      qty: Math.max(1, parseInt(formData.qty) - 1).toString(),
                    })
                  }
                >
                  -
                </button>
                <input
                  type="number"
                  value={formData.qty}
                  onChange={(e) =>
                    setFormData({ ...formData, qty: e.target.value })
                  }
                />
                <button
                  type="button"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      qty: (parseInt(formData.qty) + 1).toString(),
                    })
                  }
                >
                  +
                </button>
              </div>
            </div>
          </section>

          <section className="v-rt-create-section">
            <label className="section-label">3. Special Instructions</label>
            <textarea
              className="instructions-box"
              rows="4"
              placeholder="Any specific instructions?"
              value={formData.instructions}
              onChange={(e) =>
                setFormData({ ...formData, instructions: e.target.value })
              }
            />
          </section>
          <div style={{ height: "60px" }}></div>
        </div>

        <footer className="v-rt-create-bottom-bar">
          {isSubmitting ? (
            <div className="submitting-state">
              <FaSpinner className="spinner spin" /> Generating Ticket...
            </div>
          ) : (
            <div
              className="swipe-track"
              ref={trackRef}
              onMouseMove={handleTouchMove}
              onMouseUp={handleTouchEnd}
              onMouseLeave={handleTouchEnd}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div
                className="swipe-fill"
                style={{ width: `${swipeProgress + 28}px` }}
              ></div>
              <span className="swipe-text">Swipe to Order</span>
              <div
                className="swipe-thumb"
                style={{ transform: `translateX(${swipeProgress}px)` }}
                onMouseDown={handleTouchStart}
                onTouchStart={handleTouchStart}
              >
                <FaArrowRight className="thumb-icon" />
              </div>
            </div>
          )}
        </footer>
      </div>
    </div>
  );
};

export default CreateRetailerOrder;
